import React, { useRef, useState } from 'react';

import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { Plan } from '@cfa/pathway-fe-shared/xp';
import { TextField, TextFieldType, Typography } from '@cfa/react-components';

import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import { isValidUrl } from '@/utils/isValidUrl';
import { createCurrentLanguageValue } from '@/utils/language';

import Constants from '@/constants';

import EditButtons from './EditButtons';
import SixDotIcon from './SixDotIcon';
import { TaskValues } from './types';

import { userPrefsAtom } from '@/atoms/userPrefs';

export type { TaskValues };

const defaultValues: TaskValues = {
  task: '',
  notes: '',
  link: '',
};

interface AddTaskProps {
  isInSection: boolean;
  plan?: Plan;
  sectionId: string;
  setShowAddTask: (show: boolean) => void;
  initialValues?: TaskValues;
  onValuesChange?: (values: TaskValues) => void;
}

export const AddTask: React.FC<AddTaskProps> = ({
  sectionId,
  isInSection,
  plan,
  setShowAddTask,
  initialValues,
  onValuesChange,
}) => {
  const { addSteps } = useUpdatePlan();

  const [addTaskValues, setAddTaskValues] = useState(
    initialValues ?? defaultValues,
  );
  const [validated, setValidated] = useState(false);
  const { t } = useTranslation();
  const [linkError, setLinkError] = useState('');
  const [nameError, setNameError] = useState('');

  const nameRef = useRef<TextFieldType>(null);
  const notesRef = useRef<TextFieldType>(null);
  const linkRef = useRef<TextFieldType>(null);

  const onInputChange = (e: React.ChangeEvent<TextFieldType>) => {
    const { name, value } = e.target;
    const newValues = {
      ...addTaskValues,
      [name]: value,
    };
    setAddTaskValues(newValues);
    onValuesChange?.(newValues);
    setValidated(true);
  };

  const onCancelAddTask = () => {
    setShowAddTask(false);
  };

  const { language } = useAtomValue(userPrefsAtom);

  const onAddTask = async () => {
    if (addTaskValues.link && !isValidUrl(addTaskValues.link.trim())) {
      setLinkError(t('Input.Errors.url'));
      return;
    }

    if (addTaskValues.task.trim().length <= 0) {
      setNameError(t('Input.Errors.task'));
      return;
    }

    if (!plan) return;

    const task = {
      name: createCurrentLanguageValue(addTaskValues.task, language),
      note: createCurrentLanguageValue(addTaskValues.notes, language),
      urls:
        language === Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE
          ? [{ urlSet: { en: addTaskValues.link.trim() } }]
          : [{ urlSet: { es: addTaskValues.link.trim() } }],
      type: Constants.STEP_TYPES.TASK,
    };

    setShowAddTask(false);
    await addSteps(plan, [task], isInSection ? sectionId : undefined);
    setAddTaskValues(defaultValues);
  };

  return (
    <>
      <div className="relative mt-4 flex w-full flex-row items-center gap-4">
        <SixDotIcon position="top" />
        <div className="flex w-full flex-col items-center">
          <div className={'w-full'}>
            <Typography variant="overline3">
              {t('Generic.task')}{' '}
              <span className="text-feedback-error-base">*</span>
            </Typography>
            <TextField
              className={'mb-4'}
              data-testid="AddTaskName"
              errorText={nameError}
              fullWidth
              maxLength={Constants.FORMS.MAX_TASK_LENGTH}
              multiline
              name="task"
              onChange={onInputChange}
              onKeyDown={e => e.key === 'Enter' && notesRef.current?.focus()}
              placeholder={t('TrainingPlans.addTaskFormPlaceholderText.task')}
              ref={nameRef}
              rows={1}
              value={addTaskValues.task}
            />
            <Typography variant="overline3">{t('Generic.notes')}</Typography>
            <TextField
              className={'mb-4'}
              data-testid="AddTaskNotes"
              fullWidth
              multiline
              name="notes"
              onChange={onInputChange}
              onKeyDown={e => e.key === 'Enter' && linkRef.current?.focus()}
              placeholder={t('TrainingPlans.addTaskFormPlaceholderText.notes')}
              ref={notesRef}
              rows={1}
              value={addTaskValues.notes}
            />
            <Typography variant="overline3">{t('Generic.url')}</Typography>
            <TextField
              className={'mb-4'}
              data-testid="AddTaskUrl"
              errorText={linkError}
              fullWidth
              name="link"
              onChange={onInputChange}
              onKeyDown={e =>
                e.key === 'Enter' &&
                addTaskValues.task.trim().length > 0 &&
                onAddTask()
              }
              placeholder={t('TrainingPlans.addTaskFormPlaceholderText.link')}
              ref={linkRef}
              value={addTaskValues.link}
            />
          </div>
          <EditButtons
            onCancel={onCancelAddTask}
            onEdit={onAddTask}
            primaryButtonText={t('Button.add')}
            validated={validated}
          />
        </div>
      </div>
    </>
  );
};
